import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { filterNav, isBranchActive, isNodeActive, type HasModel, type HasPermission, type NavNode } from './types';

/** Sidebar palette — driven by the CSS custom properties both apps already define. */
const S = {
  text: 'var(--sidebar-text)',
  textMuted: 'var(--sidebar-text-muted)',
  hover: 'var(--sidebar-hover)',
  activeBg: 'var(--sidebar-active-bg)',
  activeBorder: 'var(--sidebar-active-border)',
  bg: 'var(--sidebar-bg)',
  border: 'var(--sidebar-border)',
};

export interface NavTreeProps {
  items: NavNode[];
  /** Icon-only rail. Groups become hover flyouts because labels aren't visible. */
  collapsed: boolean;
  hasPermission: HasPermission;
  /**
   * Gate for `requiresModel` entries — the per-model reports. Omit to render the tree
   * ungated (nothing is dropped for want of a model).
   */
  hasModel?: HasModel;
  /** Called after any navigation — used to close the mobile drawer. */
  onNavigate?: () => void;
  /** localStorage key for persisted group expand state. Omit to disable. */
  storageKey?: string;
  /**
   * Render every link inert — greyed out, not clickable, no route change. The tree still
   * shows (and groups still expand) so the menu doesn't jump around; it just can't be used.
   * Used by the cloud sidebar when the selected node is offline.
   */
  disabled?: boolean;
  /** Appended to each link's tooltip when `disabled`, e.g. "<site> is offline". */
  disabledReason?: string;
}

function readOpenState(key?: string): Record<string, boolean> {
  if (!key) return {};
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export default function NavTree({
  items,
  collapsed,
  hasPermission,
  hasModel,
  onNavigate,
  storageKey,
  disabled = false,
  disabledReason,
}: NavTreeProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const visible = filterNav(items, hasPermission, hasModel);

  const [open, setOpen] = useState<Record<string, boolean>>(() => readOpenState(storageKey));

  // Auto-expand whichever group owns the current route. Never auto-collapses the
  // others, so a user who opened two groups keeps both open while navigating.
  useEffect(() => {
    const owner = visible.find((n) => n.children && isBranchActive(n, location.pathname));
    if (owner && !open[owner.name]) setOpen((prev) => ({ ...prev, [owner.name]: true }));
    // `visible` is rebuilt each render; keying the effect on the pathname is what matters.
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = useCallback(
    (name: string) => {
      setOpen((prev) => {
        const next = { ...prev, [name]: !prev[name] };
        if (storageKey) {
          try {
            localStorage.setItem(storageKey, JSON.stringify(next));
          } catch {
            /* private mode — expand state just won't persist */
          }
        }
        return next;
      });
    },
    [storageKey]
  );

  return (
    <>
      {visible.map((node) => {
        if (node.section) {
          return collapsed ? (
            <div key={`sec-${node.name}`} className="my-2 mx-2" style={{ borderTop: `1px solid ${S.border}` }} />
          ) : (
            <p
              key={`sec-${node.name}`}
              className="mt-3 mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-wider"
              style={{ color: S.textMuted }}
            >
              {node.name}
            </p>
          );
        }

        if (node.render) return <div key={`slot-${node.name}`}>{node.render({ collapsed })}</div>;

        if (node.children) {
          return (
            <NavGroup
              key={node.name}
              node={node}
              collapsed={collapsed}
              expanded={!!open[node.name]}
              onToggle={() => toggle(node.name)}
              onNavigate={onNavigate}
              onJump={(to) => navigate(to)}
              disabled={disabled}
              disabledReason={disabledReason}
            />
          );
        }

        return (
          <NavLeaf
            key={node.name}
            node={node}
            collapsed={collapsed}
            onNavigate={onNavigate}
            disabled={disabled}
            disabledReason={disabledReason}
          />
        );
      })}
    </>
  );
}

/* ─── Leaf link ─── */

function NavLeaf({
  node,
  collapsed,
  onNavigate,
  indented = false,
  disabled = false,
  disabledReason,
}: {
  node: NavNode;
  collapsed: boolean;
  onNavigate?: () => void;
  indented?: boolean;
  disabled?: boolean;
  disabledReason?: string;
}) {
  const location = useLocation();
  const active = isNodeActive(node, location.pathname);
  const Icon = node.icon;

  const layout = `flex items-center rounded-lg text-[13px] transition-all ${
    collapsed ? 'justify-center px-2 py-2' : `gap-2.5 py-2 ${indented ? 'pl-3 pr-3' : 'px-3'}`
  }`;

  // Not a NavLink and not a <button>: an inert <div> can't be clicked, tabbed to, or
  // activated by keyboard, so there is no path from here to a page the node can't serve.
  if (disabled) {
    return (
      <div
        className={`${layout} cursor-not-allowed select-none opacity-50`}
        style={{ fontWeight: 500, color: S.textMuted, borderLeft: collapsed || indented ? 'none' : '3px solid transparent' }}
        aria-disabled="true"
        title={disabledReason ? `${node.name} — ${disabledReason}` : node.name}
      >
        {Icon && <Icon className="h-[18px] w-[18px] flex-shrink-0" />}
        {!collapsed && <span className="truncate">{node.name}</span>}
      </div>
    );
  }

  return (
    <NavLink
      to={node.to || '#'}
      end={node.end}
      className={layout}
      style={{
        fontWeight: active ? 600 : 500,
        color: active ? 'var(--brand)' : S.text,
        backgroundColor: active ? S.activeBg : 'transparent',
        borderLeft: collapsed || indented ? 'none' : active ? `3px solid ${S.activeBorder}` : '3px solid transparent',
      }}
      onMouseEnter={(e) => {
        if (!active) (e.currentTarget as HTMLElement).style.backgroundColor = S.hover;
      }}
      onMouseLeave={(e) => {
        if (!active) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
      }}
      // Always titled, not just when collapsed: long labels are `truncate`d in the
      // expanded rail too, and without this there is no way to read the full name.
      title={node.name}
      onClick={onNavigate}
    >
      {Icon && <Icon className="h-[18px] w-[18px] flex-shrink-0" />}
      {!collapsed && <span className="truncate">{node.name}</span>}
    </NavLink>
  );
}

/* ─── Collapsible group ─── */

function NavGroup({
  node,
  collapsed,
  expanded,
  onToggle,
  onNavigate,
  onJump,
  disabled = false,
  disabledReason,
}: {
  node: NavNode;
  collapsed: boolean;
  expanded: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
  onJump: (to: string) => void;
  disabled?: boolean;
  disabledReason?: string;
}) {
  const location = useLocation();
  const branchActive = isBranchActive(node, location.pathname);
  const Icon = node.icon;
  const children = node.children || [];

  if (collapsed) {
    return (
      <NavFlyoutGroup
        node={node}
        branchActive={branchActive}
        onNavigate={onNavigate}
        disabled={disabled}
        disabledReason={disabledReason}
        // Clicking the rail icon goes straight to the group's first page.
        onActivate={() => {
          if (disabled) return;
          const first = children.find((c) => c.to);
          if (first?.to) onJump(first.to);
        }}
      />
    );
  }

  return (
    <div>
      <button
        onClick={onToggle}
        className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] transition-all ${disabled ? 'opacity-50' : ''}`}
        style={{
          // The header stays clickable while disabled so the group can still be expanded to
          // see what the node offers once it is back — only the leaves are inert.
          fontWeight: branchActive && !disabled ? 600 : 500,
          color: branchActive && !disabled ? 'var(--brand)' : disabled ? S.textMuted : S.text,
          backgroundColor: 'transparent',
          borderLeft: '3px solid transparent',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = S.hover)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
        aria-expanded={expanded}
        // Group labels are the longest in the tree ("Camera Management") and are the
        // first to truncate, so they need the full name on hover.
        title={disabled && disabledReason ? `${node.name} — ${disabledReason}` : node.name}
      >
        {Icon && <Icon className="h-[18px] w-[18px] flex-shrink-0" />}
        <span className="flex-1 text-left truncate">{node.name}</span>
        {expanded ? (
          <ChevronDownIcon className="h-3.5 w-3.5 flex-shrink-0" />
        ) : (
          <ChevronRightIcon className="h-3.5 w-3.5 flex-shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="ml-[22px] mt-0.5 space-y-0.5 pl-2" style={{ borderLeft: `1px solid ${S.border}` }}>
          {children.map((child) => (
            <NavLeaf
              key={child.name}
              node={child}
              collapsed={false}
              onNavigate={onNavigate}
              indented
              disabled={disabled}
              disabledReason={disabledReason}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Collapsed-rail group: hover/focus flyout ─── */

const FLYOUT_CLOSE_DELAY_MS = 120;

function NavFlyoutGroup({
  node,
  branchActive,
  onNavigate,
  onActivate,
  disabled = false,
  disabledReason,
}: {
  node: NavNode;
  branchActive: boolean;
  onNavigate?: () => void;
  onActivate: () => void;
  disabled?: boolean;
  disabledReason?: string;
}) {
  const [showing, setShowing] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const Icon = node.icon;

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setShowing(false), FLYOUT_CLOSE_DELAY_MS);
  };
  useEffect(() => cancelClose, []);

  // `position: fixed` off the anchor rect — an absolutely positioned panel would
  // be clipped by the nav's own vertical scroll container.
  useLayoutEffect(() => {
    if (!showing) return;
    const rect = anchorRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.top, left: rect.right + 6 });
  }, [showing]);

  return (
    <div onMouseEnter={() => { cancelClose(); setShowing(true); }} onMouseLeave={scheduleClose}>
      <button
        ref={anchorRef}
        onClick={onActivate}
        onFocus={() => setShowing(true)}
        onBlur={scheduleClose}
        className={`flex w-full items-center justify-center rounded-lg px-2 py-2 transition-all ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
        style={{
          color: branchActive && !disabled ? 'var(--brand)' : disabled ? S.textMuted : S.text,
          backgroundColor: branchActive && !disabled ? S.activeBg : 'transparent',
        }}
        title={disabled && disabledReason ? `${node.name} — ${disabledReason}` : node.name}
      >
        {Icon && <Icon className="h-[18px] w-[18px] flex-shrink-0" />}
      </button>

      {showing && pos && (
        <div
          className="fixed z-[60] w-56 rounded-xl border shadow-lg overflow-hidden py-1.5"
          style={{
            top: Math.min(pos.top, Math.max(8, window.innerHeight - 8 - (node.children!.length * 36 + 44))),
            left: pos.left,
            borderColor: S.border,
            backgroundColor: S.bg,
          }}
        >
          <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: S.textMuted }}>
            {node.name}
          </p>
          {disabled && disabledReason && (
            <p className="px-3 pb-1.5 text-[11px]" style={{ color: S.textMuted }}>
              {disabledReason}
            </p>
          )}
          <div className="px-1.5 space-y-0.5">
            {(node.children || []).map((child) => (
              <NavLeaf
                key={child.name}
                node={child}
                collapsed={false}
                disabled={disabled}
                disabledReason={disabledReason}
                onNavigate={() => {
                  setShowing(false);
                  onNavigate?.();
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
